import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Undo2 } from 'lucide-react'
import {
  adminListRefundRequests,
  adminApproveRefundRequest,
  adminRejectRefundRequest,
  adminListDisputes,
} from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'

const STATUS_TABS = ['requested', 'approved', 'processed', 'failed', 'rejected', 'all']

const STATUS_STYLES = {
  requested: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  processed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  rejected: 'bg-gray-100 text-gray-700',
}

const DAY_MS = 24 * 60 * 60 * 1000

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—')
// Deadlines are UTC instants; render in UTC so midnight UTC is not shown as the previous day.
const fmtDateUTC = (d) => (d ? new Date(d).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—')
const remainingOf = (r) => r.donation.amount - r.donation.refundedAmount
const isOutcomeUnknown = (r) => r.status === 'approved' && !!r.error?.startsWith('Outcome unknown')
const refundCents = (r) => Math.min(r.amountCents ?? remainingOf(r), remainingOf(r))

function Badge({ value, styleMap = STATUS_STYLES, fallback = 'bg-gray-100 text-gray-700' }) {
  const cls = styleMap[value] || fallback
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium capitalize ${cls}`}>
      {value?.replace(/_/g, ' ') || '—'}
    </span>
  )
}

function refundLabel(r) {
  const remaining = remainingOf(r)
  if (r.amountCents == null || r.amountCents >= remaining) return `Full — ${money(remaining)}`
  return `${money(r.amountCents)} of ${money(remaining)}`
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider">
      {children}
    </th>
  )
}

function DisputesSection() {
  const [status, setStatus] = useState('open')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    adminListDisputes({ status })
      .then((d) => { if (!cancelled) setItems(d || []) })
      .catch(() => { if (!cancelled) { setItems([]); setLoadError(true) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [status, attempt])

  const isDue = (d) =>
    !d.closedAt && d.evidenceDueBy && new Date(d.evidenceDueBy).getTime() - Date.now() < 3 * DAY_MS

  return (
    <section id="disputes" aria-labelledby="disputes-heading" className="mt-12">
      <div className="flex items-center justify-between mb-4">
        <h2 id="disputes-heading" className="text-2xl font-bold font-display text-charcoal">Disputes</h2>
        <div className="flex gap-1 bg-white border border-surface-border rounded-lg p-1 w-fit">
          {['open', 'closed'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-1.5 rounded text-sm font-medium capitalize transition-colors ${
                status === s ? 'bg-gold text-black' : 'text-charcoal-muted hover:text-charcoal'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : loadError ? (
          <div role="alert" className="px-4 py-12 text-center text-sm text-red-800">
            Couldn't load disputes.{' '}
            <button type="button" onClick={() => setAttempt((n) => n + 1)} className="underline font-medium">
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-linen/50 border-b border-surface-border">
                <tr>
                  {['Opened', 'Parish', 'Amount', 'Reason', 'Status', 'Evidence due', 'Reversal', ''].map((h, i) => (
                    <Th key={i}>{h}</Th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-charcoal-muted text-sm">
                      No {status} disputes.
                    </td>
                  </tr>
                ) : (
                  items.map((d) => (
                    <tr key={d.id}>
                      <td className="px-4 py-3 text-sm text-charcoal whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-charcoal">{d.parishName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-charcoal">{money(d.amountCents)}</td>
                      <td className="px-4 py-3 text-sm text-charcoal capitalize">{d.reason?.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3">
                        <Badge value={d.status} />
                      </td>
                      <td
                        data-testid={`evidence-due-${d.id}`}
                        data-urgent={isDue(d) ? 'true' : 'false'}
                        className={`px-4 py-3 text-sm whitespace-nowrap ${
                          isDue(d) ? 'text-red-700 font-semibold bg-red-50' : 'text-charcoal'
                        }`}
                      >
                        {fmtDateUTC(d.evidenceDueBy)}
                      </td>
                      <td className="px-4 py-3 text-xs text-charcoal-muted font-mono">
                        {d.stripeTransferReversalId || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`https://dashboard.stripe.com/disputes/${d.stripeDisputeId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:underline"
                        >
                          Stripe
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default function Refunds() {
  const [items, setItems] = useState([])
  const [status, setStatus] = useState('requested')
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null) // { request, action }
  const [note, setNote] = useState('')
  const [acting, setActing] = useState(false)
  const [failure, setFailure] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const reqCounter = useRef(0)

  const refresh = useCallback(async () => {
    const mine = ++reqCounter.current
    setLoading(true)
    setLoadError(false)
    try {
      const data = await adminListRefundRequests({ status })
      if (mine === reqCounter.current) setItems(data || [])
    } catch {
      if (mine === reqCounter.current) { setItems([]); setLoadError(true) }
    } finally {
      if (mine === reqCounter.current) setLoading(false)
    }
  }, [status])

  useEffect(() => { refresh() }, [refresh])
  useEffect(() => { setFailure(null) }, [status])

  const openAction = (request, action) => {
    setSelected({ request, action })
    setNote('')
    setFailure(null)
  }

  const confirm = async () => {
    const { request, action } = selected
    setActing(true)
    setFailure(null)
    try {
      const updated =
        action === 'approve' || action === 'retry'
          ? await adminApproveRefundRequest(request.id, note)
          : await adminRejectRefundRequest(request.id, note.trim())
      setSelected(null)
      if (updated?.error) {
        setFailure({ action, message: updated.error })
      } else if (updated?.status === 'failed') {
        setFailure({ action, message: 'The refund failed.' })
      }
      await refresh()
    } catch (e) {
      setFailure({ action, message: e?.message || 'Request failed.' })
      if (e?.code === 'REFUND_REQUEST_NOT_PENDING') {
        setSelected(null)
        await refresh()
      }
    } finally {
      setActing(false)
    }
  }

  const rejectBlocked = selected?.action === 'reject' && !note.trim()

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal mb-1">Refunds</h1>
          <p className="text-charcoal-muted text-sm">
            {loading ? 'Loading…' : loadError ? '' : `${items.length} request${items.length !== 1 ? 's' : ''} · ${status}`}
          </p>
        </div>
        <a href="#disputes" className="text-sm text-gold hover:underline">Disputes ↓</a>
      </div>

      <div className="flex gap-1 mb-4 bg-white border border-surface-border rounded-lg p-1 w-fit">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-4 py-1.5 rounded text-sm font-medium capitalize transition-colors ${
              status === s ? 'bg-gold text-black' : 'text-charcoal-muted hover:text-charcoal'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {failure && (
        <div role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {failure.action === 'reject' ? 'Reject' : failure.action === 'retry' ? 'Retry' : 'Approve'} failed: {failure.message}
        </div>
      )}

      <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : loadError ? (
          <div role="alert" className="px-4 py-12 text-center text-sm text-red-800">
            Couldn't load refund requests.{' '}
            <button type="button" onClick={refresh} className="underline font-medium">
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-linen/50 border-b border-surface-border">
                <tr>
                  {['Requested', 'Parish', 'Donor', 'Donation', 'Refund amount', 'Reason', 'Status', 'Actions'].map((h) => (
                    <Th key={h}>{h}</Th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <Undo2 className="w-7 h-7 text-charcoal-subtle mx-auto mb-2" />
                      <p className="text-charcoal-muted text-sm">No {status} refund requests.</p>
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id} className="align-top hover:bg-surface-linen/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-charcoal whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-charcoal">{r.parishName}</td>
                      <td className="px-4 py-3 text-sm text-charcoal">
                        <div>{r.donation.donorName || '—'}</div>
                        <div className="text-xs text-charcoal-muted">{r.donation.donorEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-charcoal">
                        <div>{money(r.donation.amount)}</div>
                        <div className="text-xs text-charcoal-muted">
                          {r.donation.donationType?.replace(/_/g, ' ')} · {fmtDate(r.donation.completedAt)}
                          {r.donation.paymentMethodLast4 ? ` · •••• ${r.donation.paymentMethodLast4}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-charcoal whitespace-nowrap">{refundLabel(r)}</td>
                      <td className="px-4 py-3 text-sm text-charcoal/90 max-w-xs">{r.reason}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge value={r.status} />
                        {(r.error || r.reviewNote) && (
                          <div className={`mt-1 text-xs ${r.error ? 'text-red-700' : 'text-charcoal-muted'}`}>
                            {r.error || r.reviewNote}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.status === 'requested' && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => openAction(r, 'approve')} className="text-xs">
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openAction(r, 'reject')}
                              className="text-xs text-charcoal-muted hover:text-charcoal"
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                        {isOutcomeUnknown(r) && (
                          <Button size="sm" onClick={() => openAction(r, 'retry')} className="text-xs">
                            Retry refund
                          </Button>
                        )}
                        {selected?.request.id === r.id && (
                          <div className="mt-3 w-72 rounded border border-surface-border bg-surface-linen/30 p-3 space-y-2">
                            {selected.action === 'retry' && (
                              <p className="text-sm text-charcoal">
                                {`Retry the refund of ${money(refundCents(r))}. Stripe deduplicates by request, so the donor is refunded at most once. Check the Stripe dashboard first if unsure.`}
                              </p>
                            )}
                            {selected.action === 'approve' && (
                              <p className="text-sm text-charcoal">
                                {`Refund ${money(refundCents(r))} to the donor. The transfer to ${r.parishName} is reversed and the platform fee refunded.`}
                              </p>
                            )}
                            <Label htmlFor={`note-${r.id}`}>
                              {selected.action === 'reject' ? 'Note (required)' : 'Note (optional)'}
                            </Label>
                            <textarea
                              id={`note-${r.id}`}
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              rows={3}
                              className="w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={confirm} disabled={acting || rejectBlocked} className="text-xs">
                                {acting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelected(null)}
                                disabled={acting}
                                className="text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DisputesSection />
    </div>
  )
}
